import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import classNames from 'classnames';
import Button from '@helsenorge/designsystem-react/components/Button';
import Checkbox from '@helsenorge/designsystem-react/components/Checkbox';
import Chip from '@helsenorge/designsystem-react/components/Chip';
import Expander, { ExpanderSize } from '@helsenorge/designsystem-react/components/Expander';
import FormGroup from '@helsenorge/designsystem-react/components/FormGroup';
import Icon from '@helsenorge/designsystem-react/components/Icon';
import ChevronLeft from '@helsenorge/designsystem-react/components/Icons/ChevronLeft';
import NoFilter from '@helsenorge/designsystem-react/components/Icons/NoFilter';
import Loader from '@helsenorge/designsystem-react/components/Loader';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import Tag from '@helsenorge/designsystem-react/components/Tag';
import TagList from '@helsenorge/designsystem-react/components/TagList';
import { isComponent } from '@helsenorge/designsystem-react/utils/component';
import { useLanguage } from '@helsenorge/designsystem-react/utils/language';
import { isMobileUA } from '@helsenorge/designsystem-react/utils/mobile';
import uuid from '@helsenorge/designsystem-react/utils/uuid';
import LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { DateFormat, safeFormatCET } from '@helsenorge/core-utils/date-fns-utils';
import FilterGroup from './filter-group';
import FilterSort from './filter-sort';
import { ResourcesContextProvider } from './resources';
import { getResources } from '../../resources';
import OffCanvas from '../offcanvas';
import styles from './styles.module.scss';
export var Status;
(function (Status) {
    Status["Ready"] = "ready";
    Status["LoadingResults"] = "loading-results";
})(Status || (Status = {}));
const isSortFilterOption = (option) => {
    return option.type === 'sort' || option.type === 'sort-select' || option.type === 'sort-select-responsive';
};
const moveSortLast = (a, b) => {
    const aIsSort = isSortFilterOption(a);
    const bIsSort = isSortFilterOption(b);
    if (aIsSort && !bIsSort) {
        return 1; // Move filter-sort towards the end
    }
    else if (!aIsSort && bIsSort) {
        return -1; // Keep other types towards the beginning
    }
    return 0; // Keep original order if same type
};
export const Filter = props => {
    const { afterHeadingChildren, afterActiveFiltersChildren, activeFilters, extraFilterOptions, showFilters = false, filterOptions, footerContent, isAlwaysOpen, isDisabled, allowMobileView = true, numResults, status, statusText, onFilterOpen, onSubmit, onResetActiveFilters, searchBelowFilter, searchFilter, } = props;
    const { language } = useLanguage(LanguageLocales.NORWEGIAN);
    const defaultResources = getResources(language);
    const resources = { ...defaultResources, ...props.resources };
    const isMobile = allowMobileView && isMobileUA();
    const [prevActiveFilters, setPrevActiveFilters] = React.useState(activeFilters);
    const [showFiltersState, setShowFiltersState] = React.useState(!isMobile && showFilters);
    const isLoading = status === Status.LoadingResults;
    const isSlowSearch = typeof onSubmit !== 'undefined';
    React.useEffect(() => {
        if (!isMobile && typeof showFilters !== 'undefined') {
            setShowFiltersState(showFilters);
        }
    }, [showFilters]);
    React.useEffect(() => {
        if (onFilterOpen) {
            onFilterOpen(showFiltersState);
        }
    }, [showFiltersState]);
    const toggleShowMainFilter = () => {
        if (!isDisabled) {
            setShowFiltersState(!showFiltersState);
        }
    };
    const onSubmitHandler = () => {
        setPrevActiveFilters(activeFilters ?? []);
        if (onSubmit) {
            onSubmit();
        }
        if (isMobile) {
            toggleShowMainFilter();
        }
    };
    const isFilterResultSubmitted = () => {
        return JSON.stringify(prevActiveFilters) === JSON.stringify(activeFilters);
    };
    const resetActiveFilters = () => {
        if (!activeFilters)
            return;
        Promise.all(activeFilters.map(af => {
            af.handleRemove();
        })).then(() => {
            if (onResetActiveFilters) {
                onResetActiveFilters();
            }
        });
    };
    const activeFilterTag = (af) => {
        if (af.value instanceof Date) {
            return safeFormatCET(af.value, DateFormat.ShortDate);
        }
        else if (typeof af.value === 'object') {
            return af.value?.displayText ?? af.value?.value;
        }
        else if (af.value !== undefined) {
            return af.value;
        }
        else {
            return '';
        }
    };
    const renderActiveFilters = () => activeFilters &&
        activeFilters.length > 0 && (_jsx("div", { className: classNames(styles['active-filters'], {
            [styles['active-filters--desktop']]: !isMobile,
            [styles['active-filters--no-bottom-spacing']]: (!isMobile && searchBelowFilter && showFiltersState) || afterActiveFiltersChildren,
        }), children: !isLoading && (_jsxs(_Fragment, { children: [_jsx("span", { className: styles['active-filters__label'], children: resources.selectedFilters }), _jsx("div", { className: styles['active-filters__tags-container'], children: isSlowSearch ? renderTags(activeFilters) : renderChips(activeFilters) })] })) }));
    const renderTags = (activeFilters) => {
        return (_jsx(TagList, { children: activeFilters.map(af => (_jsx("span", { className: styles['active-filters__tag'], children: _jsx(Tag, { color: "blueberry", size: "medium", variant: "normal", children: activeFilterTag(af) }) }, af.name))) }));
    };
    const onActiveFilterClicked = (activeFilter) => {
        activeFilter.handleRemove().then(() => {
            if (onResetActiveFilters) {
                onResetActiveFilters(activeFilter);
            }
        });
    };
    const renderChips = (activeFilters) => {
        return (_jsxs(TagList, { children: [activeFilters.map(af => (_jsx("span", { className: styles['active-filters__tag'], children: _jsx(Chip, { action: "remove", color: "blueberry", onClick: () => onActiveFilterClicked(af), size: "medium", variant: "normal", children: activeFilterTag(af) }) }, af.name))), activeFilters.length > 1 && (_jsxs(Button, { onClick: resetActiveFilters, variant: 'borderless', children: [_jsx(Icon, { svgIcon: NoFilter }), 'Nullstill filter'] }))] }));
    };
    const renderFilterOptions = (filterOptions, isInsideFilter) => {
        const sortedFilterOptions = isMobile ? [...filterOptions].sort(moveSortLast) : filterOptions;
        const renderChildren = (children) => {
            return React.Children.map(children, (child, index) => {
                if (isComponent(child, Checkbox)) {
                    return (_jsx("div", { className: styles['filter-options__checkbox'], children: child }, child.key || index + uuid()));
                }
                if (isComponent(child, React.Fragment)) {
                    return React.Children.map(child.props.children, (child) => {
                        return renderChildren(child);
                    });
                }
                return _jsx("div", { className: styles['filter-options__item'], children: child });
            });
        };
        return sortedFilterOptions.map((fo, index) => {
            switch (fo.type) {
                case 'inline':
                    return renderChildren(fo.children);
                case 'sort':
                case 'sort-select':
                case 'sort-select-responsive': {
                    return (_jsx(FilterSort, { selectClasses: classNames({ [styles['filter-sort-wrapper--right']]: !isMobile && !isInsideFilter }), sortKey: fo.defaultValue, isMobile: fo.type === 'sort' && isMobile, responsiveSelect: fo.type === 'sort-select-responsive', onSort: fo.onSort, label: fo.label, toggleShowFilters: !onSubmit && isInsideFilter ? toggleShowMainFilter : undefined, children: fo.children }, index + uuid()));
                }
                case 'dropdown': {
                    return (_jsx(FilterGroup, { dropdownMinWidth: fo.dropdownMinWidth, onClose: fo.onClose, isMobile: isMobile, label: fo.label, placeholder: fo.placeholder, children: fo.children }));
                }
            }
        });
    };
    const renderSlowSearchSubmitButton = () => {
        return isSlowSearch ? (_jsx(Button, { onClick: onSubmitHandler, disabled: isFilterResultSubmitted(), wrapperClassName: classNames(!isMobile && styles['filter-options__submit-button-desktop']), children: isFilterResultSubmitted() ? resources.updateFilterButtonDisabledText : resources.updateFilterButtonText })) : isMobile ? (_jsx(Button, { onClick: toggleShowMainFilter, children: resources.showResults })) : null;
    };
    const renderFilter = isLoading ? (_jsx(Loader, { center: true, size: 'small', ariaLabel: resources.loadingResults })) : (_jsxs("div", { className: styles['filters-content'], children: [searchFilter && !searchBelowFilter && _jsx("div", { children: searchFilter }), filterOptions && (_jsxs(FormGroup, { htmlMarkup: 'div', fieldsetClassName: styles['filter-options'], renderError: false, children: [renderFilterOptions(filterOptions, true), !isMobile && renderSlowSearchSubmitButton()] })), extraFilterOptions && (_jsx(Expander, { title: resources.showSecondaryFilters, children: _jsx(FormGroup, { htmlMarkup: 'div', fieldsetClassName: classNames(styles['filter-options'], styles['filter-options--extra']), renderError: false, children: renderFilterOptions(extraFilterOptions, true) }) }))] }));
    const offCanvasHeader = (_jsxs(_Fragment, { children: [isSlowSearch && (_jsxs(_Fragment, { children: [_jsxs(Button, { variant: 'borderless', onClick: () => {
                            toggleShowMainFilter();
                        }, children: [_jsx(Icon, { svgIcon: ChevronLeft }), resources.cancel] }), _jsx(Spacer, { size: '2xs' })] })), renderSlowSearchSubmitButton()] }));
    const renderDesktopFilter = (children) => {
        const renderAfterHeadingChildren = afterHeadingChildren && (_jsx("div", { className: classNames(styles['after-heading'], { [styles['after-heading--search-below-filter']]: searchBelowFilter }), children: afterHeadingChildren }));
        const noBottomPaddingClass = classNames({
            [styles['filter-desktop--no-bottom-spacing']]: searchBelowFilter || (activeFilters && activeFilters.length > 0),
        });
        return isAlwaysOpen ? (_jsxs("div", { className: classNames(styles['filter-desktop'], noBottomPaddingClass), children: [_jsxs("div", { className: styles['title-container'], children: [_jsx("span", { className: styles['title'], children: resources.filterTitle }), renderAfterHeadingChildren] }), children] })) : (_jsxs(Expander, { renderChildrenWhenClosed: true, size: ExpanderSize.large, onExpand: (expanded) => setShowFiltersState(expanded), expanded: showFiltersState, noNestedLine: true, title: resources.filterTitle, contentClassNames: noBottomPaddingClass, children: [renderAfterHeadingChildren, children] }));
    };
    const counter = !isLoading ? _jsx("span", { className: styles['numresults'], children: numResults }) : null;
    return (_jsxs(ResourcesContextProvider, { value: resources, children: [isMobile ? (_jsxs(_Fragment, { children: [_jsx(Button, { "aria-expanded": showFiltersState, onClick: toggleShowMainFilter, arrow: "icon", fluid: true, variant: 'outline', children: resources.toggleFilterAndSort }), _jsxs(OffCanvas, { header: offCanvasHeader, isLoading: isLoading, isOpened: showFiltersState, onClose: toggleShowMainFilter, resetActiveFilters: activeFilters && activeFilters?.length > 0 ? resetActiveFilters : undefined, children: [_jsxs("div", { className: styles['title-container'], children: [_jsx("span", { className: styles['title'], children: resources.filterHeading }), afterHeadingChildren && (_jsx("div", { className: classNames(styles['after-heading'], { [styles['after-heading--mobile']]: isMobile }), children: afterHeadingChildren })), _jsxs("span", { "aria-live": "polite", "aria-atomic": "true", children: [_jsxs("span", { className: styles['numresults--hidden'], children: [resources.numResults, " "] }), counter] })] }), renderFilter] })] })) : (_jsx("div", { className: styles['filters-wrapper'], children: renderDesktopFilter(renderFilter) })), renderActiveFilters(), afterActiveFiltersChildren && (_jsx("div", { className: classNames(styles['after-active-filters'], {
                    [styles['after-active-filters--desktop']]: !isMobile,
                }), children: afterActiveFiltersChildren })), searchFilter && searchBelowFilter && !isLoading && (showFiltersState || isMobile) && (_jsx("div", { className: classNames(styles['filter-search-below-filter-wrapper'], {
                    [styles['filter-search-below-filter-wrapper--mobile']]: isMobile,
                }), children: searchFilter })), _jsxs("div", { className: classNames(styles['filter-footer'], { [styles['filter-footer--is-mobile']]: isMobile }), children: [statusText && (_jsx("span", { className: styles['filter-footer__numresults'], "aria-live": "polite", "aria-atomic": "true", role: "status", children: isLoading ? (_jsx(Loader, { inline: true, size: 'tiny', ariaLabel: resources.loadingResults })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: styles['filter-footer__numresults--hidden'], children: resources.numResults }), ` ${statusText}`] })) })), footerContent && (_jsx(FormGroup, { className: styles['filter-options-wrapper'], htmlMarkup: 'div', fieldsetClassName: styles['filter-options'], renderError: false, children: renderFilterOptions(footerContent, false) }))] })] }));
};
export default Filter;
